/**
 * Particle-trail cursor (port of music.daydream.live's effect).
 *
 * Keeps the native arrow cursor and trails a comet of soft sparks behind it:
 * sparks drift, fall under slight gravity, and fade over ~0.4 s. Clicking
 * bursts ~16 sparks outward. After 2 s idle the whole canvas fades out.
 *
 * Single full-viewport <canvas class="fx-cursor-canvas"> with
 * mix-blend-mode: screen (set in global.css), so the sparks read as a luminous
 * glow over the dark site background; over the white legal/privacy doc cards
 * they fade out, which is fine now that there is no dot to keep visible.
 *
 * Palette is fx blue (--fx-blue-5/4/3) with one warm amber accent (~1-in-4
 * sparks), so the trail reads blue with warm flecks.
 *
 * Hard limits:
 *  - Skipped under prefers-reduced-motion (native cursor kept).
 *  - Skipped on touch / coarse-pointer devices (no hover).
 *  - rAF loop pauses while the tab is hidden.
 *  - Particle pool capped at 90 (oldest dropped).
 *  - Idempotent: re-invoking initCursor() is a no-op after the first call.
 */

// fx blue brand + one warm amber accent. Listed 3 blue : 1 warm, so roughly a
// quarter of sparks are warm — blue trail with warm flecks.
const PALETTE = ["#0088e9", "#3ca4ee", "#87c7f5", "#f0a23c"] as const;

const MAX_PARTICLES = 90;
const MOVE_THRESHOLD = 5; // px of travel before a trail spark spawns
const TRAIL_LIFE_MS = 420;
const BURST_COUNT = 16;
const BURST_LIFE_MS = 900;
const GRAVITY = 0.16; // per 60 fps-normalised step
const IDLE_MS = 2000; // pointer-still delay before the canvas fades out
const MAX_DPR = 2;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
  color: string;
}

let started = false;

export function initCursor(): void {
  if (started) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    console.log("[cursor] skipped: prefers-reduced-motion");
    return;
  }
  // Touch / coarse pointers have no hovering cursor to replace.
  if (window.matchMedia("(hover: none), (pointer: coarse)").matches) {
    console.log("[cursor] skipped: no fine pointer");
    return;
  }
  started = true;

  const canvas = document.createElement("canvas");
  canvas.className = "fx-cursor-canvas";
  canvas.setAttribute("aria-hidden", "true");
  const ctx = canvas.getContext("2d", { desynchronized: true });
  if (!ctx) {
    started = false;
    return;
  }
  document.body.appendChild(canvas);

  let cssW = window.innerWidth;
  let cssH = window.innerHeight;

  function resize(): void {
    const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
    cssW = window.innerWidth;
    cssH = window.innerHeight;
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  resize();

  const particles: Particle[] = [];
  function spawn(
    x: number,
    y: number,
    vx: number,
    vy: number,
    life: number
  ): void {
    if (particles.length >= MAX_PARTICLES) particles.shift();
    const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
    particles.push({ x, y, vx, vy, age: 0, life, color });
  }

  let mx = cssW / 2;
  let my = cssH / 2;
  let px = mx;
  let py = my;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;

  function bumpIdle(): void {
    document.body.classList.remove("fx-cursor-idle");
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(
      () => document.body.classList.add("fx-cursor-idle"),
      IDLE_MS
    );
  }

  function onMove(e: MouseEvent): void {
    mx = e.clientX;
    my = e.clientY;
    const dx = mx - px;
    const dy = my - py;
    if (Math.hypot(dx, dy) > MOVE_THRESHOLD) {
      // Slight backward kick relative to travel, plus jitter.
      spawn(
        mx,
        my,
        -0.06 * dx + (Math.random() - 0.5) * 1.5,
        -0.06 * dy + (Math.random() - 0.5) * 1.5,
        TRAIL_LIFE_MS
      );
      px = mx;
      py = my;
    }
    bumpIdle();
  }

  function onDown(e: MouseEvent): void {
    for (let i = 0; i < BURST_COUNT; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 3.2 + 3.8 * Math.random();
      spawn(e.clientX, e.clientY, Math.cos(a) * sp, Math.sin(a) * sp - 1.2, BURST_LIFE_MS);
    }
    bumpIdle();
  }

  window.addEventListener("resize", resize, { passive: true });
  document.addEventListener("mousemove", onMove, { passive: true });
  document.addEventListener("mousedown", onDown, { passive: true });
  bumpIdle();

  let last = 0;
  function tick(now: number): void {
    requestAnimationFrame(tick);
    if (document.hidden) {
      last = 0;
      return;
    }
    const dt = last ? Math.min(50, now - last) : 16;
    last = now;
    const s = dt / 16; // 60 fps-normalised step

    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.age += dt;
      if (p.age >= p.life) {
        particles.splice(i, 1);
        continue;
      }
      p.vy += GRAVITY * s;
      p.x += p.vx * s;
      p.y += p.vy * s;
    }

    ctx!.clearRect(0, 0, cssW, cssH);
    for (const p of particles) {
      const t = p.age / p.life;
      const r = 2 * (1 - 0.7 * t);
      if (r <= 0.1) continue;
      ctx!.globalAlpha = 1 - t;
      ctx!.fillStyle = p.color;
      ctx!.beginPath();
      ctx!.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx!.fill();
    }
    ctx!.globalAlpha = 1;
  }
  requestAnimationFrame(tick);
}
